"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, ShieldCheck, Cpu, Lightbulb, Car, FileCheck, Clock, ArrowRight, CircleCheck as CheckCircle2, PhoneCall, SlidersHorizontal as Sliders, Maximize2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type Language = "es" | "en" | "eu" | "fr";
const LANGUAGE_STORAGE_KEY = "wvc_language";
const LANGUAGE_EVENT_NAME = "wvc-language-change";
function useLanguage(): [Language, (lang: Language) => void] {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window === "undefined") return "es";
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
      if (stored === "es" || stored === "en" || stored === "eu" || stored === "fr") {
        return stored;
      }
    } catch {
      // Ignore storage access errors
    }
    return "es";
  });
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<Language>;
      if (customEvent.detail) {
        setLanguageState(customEvent.detail);
      } else {
        try {
          const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
          if (stored === "es" || stored === "en" || stored === "eu" || stored === "fr") {
            setLanguageState(stored);
          }
        } catch {
          // Ignore storage access errors
        }
      }
    };
    window.addEventListener(LANGUAGE_EVENT_NAME, handleLanguageChange);
    window.addEventListener("storage", handleLanguageChange);
    return () => {
      window.removeEventListener(LANGUAGE_EVENT_NAME, handleLanguageChange);
      window.removeEventListener("storage", handleLanguageChange);
    };
  }, []);
  const setLanguage = (newLang: Language) => {
    setLanguageState(newLang);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
      window.dispatchEvent(new CustomEvent<Language>(LANGUAGE_EVENT_NAME, {
        detail: newLang
      }));
    } catch {
      // Ignore storage access errors
    }
  };
  return [language, setLanguage];
}
const translations = {
  es: {
    eyebrow: "Técnicos Electricistas & Soluciones Eléctricas",
    heading: "Servicios eléctricos de precisión",
    subheading: "Desde intervenciones de urgencia 24/7 hasta complejos proyectos de domótica Loxone e iluminación arquitectónica en Bilbao, sus alrededores y en todo el País Vasco.",
    loxoneBadge: "Partner Oficial Loxone",
    loxoneDesc1: "Automatización integral del hogar: iluminación, clima, persianas, audio multiroom y seguridad gestionados por un único cerebro Loxone Miniserver.",
    loxoneDesc2: "Control total centralizado, máxima eficiencia energética y automatización inteligente con estándar industrial.",
    consultService: "Consultar servicio",
    portfolioEyebrow: "Trabajo real, acabado impecable",
    portfolioHeading: "Nuestros trabajos",
    portfolioDesc: "Muestra gráfica de nuestras ejecuciones e instalaciones domóticas e industriales en Bilbao, sus alrededores y en todo el País Vasco.",
    requestQuote: "Solicitar presupuesto",
    services: [{
      id: "urgencias",
      badge: "24/7",
      tag: "URGENCIAS & AVERÍAS",
      title: "Averías & Urgencias 24/7",
      description: "Localización y reparación rápida de fallos eléctricos en viviendas, locales e industria. Respuesta inmediata en Bilbao, sus alrededores y en todo el País Vasco.",
      highlights: ["Respuesta inmediata", "Asistencia en Bilbao, alrededores y todo el País Vasco", "Diagnóstico térmico"]
    }, {
      id: "reformas",
      badge: "RESIDENCIAL & COMERCIAL",
      tag: "NORMATIVA VIGENTE",
      title: "Reformas Eléctricas Integrales",
      description: "Renovación completa de instalaciones en pisos, locales comerciales y oficinas, adaptadas al REBT y a tu consumo real.",
      highlights: ["Adaptación al REBT", "Optimización de potencia", "Materiales de alta durabilidad"]
    }, {
      id: "cuadros",
      badge: "NORMATIVA REBT",
      tag: "ELECTRICISTAS EXPERTOS",
      title: "Cuadros Eléctricos & Control",
      description: "Diseño, montaje y legalización de cuadros de distribución y automatismos de alta precisión para cualquier potencia.",
      highlights: ["Loxon y automatismos", "Protección contra sobretensiones", "Ensamblaje certificado"]
    }, {
      id: "iluminacion",
      badge: "DISEÑO DE LUZ",
      tag: "EFICIENCIA LED",
      title: "Iluminación Arquitectónica & LED",
      description: "Proyectos de luz a medida que realzan espacios y reducen consumo: LED, escenas y control inteligente.",
      highlights: ["Diseño de escenas", "Integración Domótica", "Máximo ahorro energético"]
    }, {
      id: "recarga-ve",
      badge: "MOVES III",
      tag: "MOVILIDAD ELÉCTRICA",
      title: "Puntos de Recarga VE",
      description: "Instalación de cargadores para coche eléctrico en garajes, comunidades y empresas. Gestión de subvenciones MOVES III.",
      highlights: ["Cargadores inteligentes", "Tramitación Subvención MOVES", "Instalaciones en garajes de comunidad"]
    }, {
      id: "boletines",
      badge: "INSTALADOR AUTORIZADO",
      tag: "CERTIFICACIÓN OFICIAL",
      title: "Boletines & Legalizaciones",
      description: "Boletines eléctricos, dictámenes y legalización de instalaciones con tramitación ágil ante Industria.",
      highlights: ["Certificados CIE / BRIE", "Gestión directa con Industria", "Revisión previa de seguridad"]
    }],
    works: [{
      title: "Automatización domótica Loxone en vivienda unifamiliar",
      category: "Loxone Smart Home"
    }, {
      title: "Reforma de cuadro general e iluminación en oficina comercial",
      category: "Instalación Comercial"
    }, {
      title: "Infraestructura de recarga doble VE en garaje privado",
      category: "Puntos de Recarga VE"
    }, {
      title: "Instalación de iluminación LED integrada en esquina",
      category: "Iluminación LED"
    }, {
      title: "Integración de domótica Loxone con audio y redes",
      category: "Loxone Smart Home"
    }]
  },
  en: {
    eyebrow: "Electrical Technicians & Electrical Solutions",
    heading: "Precision Electrical Services",
    subheading: "From 24/7 emergency interventions to complex Loxone home automation and architectural lighting projects in Bilbao, surroundings, and across the Basque Country.",
    loxoneBadge: "Official Loxone Partner",
    loxoneDesc1: "Comprehensive home automation: lighting, climate, blinds, multiroom audio, and security managed by a single Loxone Miniserver brain.",
    loxoneDesc2: "Total centralized control, maximum energy efficiency, and intelligent industrial-grade automation.",
    consultService: "Inquire about service",
    portfolioEyebrow: "Real work, flawless finish",
    portfolioHeading: "Our Work",
    portfolioDesc: "Visual showcase of our home automation and industrial electrical installations in Bilbao, surroundings, and across the Basque Country.",
    requestQuote: "Request a quote",
    services: [{
      id: "urgencias",
      badge: "24/7",
      tag: "EMERGENCIES & BREAKDOWNS",
      title: "Breakdowns & Emergencies 24/7",
      description: "Fast troubleshooting and repair of electrical faults in homes, commercial premises, and industry. Immediate response in Bilbao, surroundings, and across the Basque Country.",
      highlights: ["Immediate response", "Assistance in Bilbao, surroundings, and Basque Country", "Thermal diagnostics"]
    }, {
      id: "reformas",
      badge: "RESIDENTIAL & COMMERCIAL",
      tag: "CURRENT REGULATIONS",
      title: "Complete Electrical Renovations",
      description: "Comprehensive renewal of electrical installations in apartments, commercial spaces, and offices, adapted to REBT regulations and actual consumption.",
      highlights: ["REBT compliance", "Power optimization", "High-durability materials"]
    }, {
      id: "cuadros",
      badge: "REBT REGULATIONS",
      tag: "EXPERT ELECTRICIANS",
      title: "Electrical Panels & Control",
      description: "Design, assembly, and certification of high-precision distribution panels and automated systems for any power capacity.",
      highlights: ["Loxone & automation", "Surge protection", "Certified assembly"]
    }, {
      id: "iluminacion",
      badge: "LIGHTING DESIGN",
      tag: "LED EFFICIENCY",
      title: "Architectural & LED Lighting",
      description: "Custom lighting projects that enhance architectural spaces and reduce consumption: LED, scenes, and smart control.",
      highlights: ["Scene design", "Home automation integration", "Maximum energy savings"]
    }, {
      id: "recarga-ve",
      badge: "MOVES III",
      tag: "ELECTRIC MOBILITY",
      title: "EV Charging Points",
      description: "Installation of EV chargers in garages, community car parks, and businesses. MOVES III subsidy management.",
      highlights: ["Smart chargers", "MOVES subsidy processing", "Community garage installations"]
    }, {
      id: "boletines",
      badge: "AUTHORIZED INSTALLER",
      tag: "OFFICIAL CERTIFICATION",
      title: "Compliance Certificates & Approvals",
      description: "Official electrical certificates, technical reports, and legalizations with streamlined processing before the Ministry of Industry.",
      highlights: ["CIE / BRIE certificates", "Direct administrative processing", "Prior safety inspection"]
    }],
    works: [{
      title: "Loxone home automation in single-family residence",
      category: "Loxone Smart Home"
    }, {
      title: "Main distribution panel renovation and lighting in commercial office",
      category: "Commercial Installation"
    }, {
      title: "Dual EV charging infrastructure in private garage",
      category: "EV Charging Points"
    }, {
      title: "Corner-integrated LED lighting installation",
      category: "LED Lighting"
    }, {
      title: "Loxone home automation integration with audio and networking",
      category: "Loxone Smart Home"
    }]
  },
  eu: {
    eyebrow: "Teknikari Elektrizistak & Soluzio Elektrikoak",
    heading: "Zehaztasun handiko zerbitzu elektrikoak",
    subheading: "24/7 larrialdietatik hasi eta Loxone domotika eta argiztapen arkitektonikoko proiektu konplexuetaraino Bilbon, inguruetan eta Euskal Herri osoan.",
    loxoneBadge: "Loxone Partner Ofiziala",
    loxoneDesc1: "Etxeko automatizazio integrala: argiztapena, klimatizazioa, pertsianak, multiroom audioa eta segurtasuna, Loxone Miniserver buru bakarrak kudeatuta.",
    loxoneDesc2: "Kontrol zentralizatu osoa, energia-eraginkortasun handiena eta industria-mailako automatizazio adimenduna.",
    consultService: "Zerbitzua kontsultatu",
    portfolioEyebrow: "Benetako lana, akabera ezin hobea",
    portfolioHeading: "Gure lanak",
    portfolioDesc: "Bilbon, inguruetan eta Euskal Herri osoan egindako domotika eta industria instalazioen erakusleiho grafikoa.",
    requestQuote: "Aurrekontua eskatu",
    services: [{
      id: "urgencias",
      badge: "24/7",
      tag: "LARRIALDIAK & AKATSAK",
      title: "Akatsak & Larrialdiak 24/7",
      description: "Akats elektrikoen bilaketa eta konponketa azkarra etxebizitzetan, lokaletan eta industrian. Berehalako erantzuna Bilbon, inguruetan eta Euskal Herri osoan.",
      highlights: ["Berehalako erantzuna", "Laguntza Bilbon, inguruetan eta Euskal Herrian", "Diagnostiko termikoa"]
    }, {
      id: "reformas",
      badge: "BIZITEGIKOA & MERKATARITZAKOA",
      tag: "INDARREKO ARAUDIA",
      title: "Erreforma Elektriko Integralak",
      description: "Instalazioen eraberritze osoa pisuetan, merkataritza-lokaletan eta bulegoetan, REBT araudira eta benetako kontsumora egokituta.",
      highlights: ["REBT araudira egokitzea", "Potentziaren optimizazioa", "Iraunkortasun handiko materialak"]
    }, {
      id: "cuadros",
      badge: "REBT ARAUDIA",
      tag: "ELEKTRIZISTA ADITUAK",
      title: "Koadro Elektrikoak & Kontrola",
      description: "Zehaztasun handiko banaketa-koadroen eta automatismoen diseinua, muntaketa eta legeztatzea edozein potentziatarako.",
      highlights: ["Loxone eta automatismoak", "Gain-tentsioen aurkako babesa", "Muntaketa ziurtatua"]
    }, {
      id: "iluminacion",
      badge: "ARGIAREN DISEINUA",
      tag: "LED ERAGINKORTASUNA",
      title: "Argiztapen Arkitektonikoa & LED",
      description: "Guneak nabarmentzen eta kontsumoa murrizten duten neurrira egindako argi-proiektuak: LED, eszenak eta kontrol adimenduna.",
      highlights: ["Eszenen diseinua", "Domotika integrazioa", "Energia aurrezpen handiena"]
    }, {
      id: "recarga-ve",
      badge: "MOVES III",
      tag: "MUGIKORTASUN ELEKTRIKOA",
      title: "IB Karga Puntuak",
      description: "Ibilgailu elektrikoen kargagailuen instalazioa garajeetan, komunitateetan eta enpresetan. MOVES III dirulaguntzen kudeaketa.",
      highlights: ["Kargagailu adimendunak", "MOVES dirulaguntzaren izapidetzea", "Instalazioak garaje komunitarioetan"]
    }, {
      id: "boletines",
      badge: "INSTALATZAILE BAIMENTSUA",
      tag: "ZIURTAGIRI OFIZIALA",
      title: "Buletinak & Legeztatzeak",
      description: "Buletin elektrikoak, irizpenak eta instalazioen legeztatzea Industriaren aurreko izapide arinekin.",
      highlights: ["CIE / BRIE ziurtagiriak", "Zuzeneko kudeaketa Industriarekin", "Segurtasunaren aldez aurreko berrikuspena"]
    }],
    works: [{
      title: "Loxone domotika automatizazioa familia bakarreko etxean",
      category: "Loxone Smart Home"
    }, {
      title: "Koadro orokorraren eta argiztapenaren erreforma merkataritza bulegoan",
      category: "Merkataritza Instalazioa"
    }, {
      title: "IB karga bikoitzeko azpiegitura garaje pribatuan",
      category: "IB Karga Puntuak"
    }, {
      title: "Eskineran integratutako LED argiztapen instalazioa",
      category: "LED Argiztapena"
    }, {
      title: "Loxone domotika integrazioa audio eta sareekin",
      category: "Loxone Smart Home"
    }]
  },
  fr: {
    eyebrow: "Électriciens Techniciens & Solutions Électriques",
    heading: "Services électriques de précision",
    subheading: "Des interventions d'urgence 24/7 aux projets complexes de domotique Loxone et d'éclairage architectural à Bilbao, ses environs et dans tout le Pays Basque.",
    loxoneBadge: "Partenaire Officiel Loxone",
    loxoneDesc1: "Automatisation intégrale de l'habitat : éclairage, climatisation, stores, audio multiroom et sécurité gérés par un cerveau unique Loxone Miniserver.",
    loxoneDesc2: "Contrôle total centralisé, efficacité énergétique maximale et automatisation intelligente aux normes industrielles.",
    consultService: "Consulter le service",
    portfolioEyebrow: "Travail réel, finition impeccable",
    portfolioHeading: "Nos réalisations",
    portfolioDesc: "Aperçu de nos réalisations et installations domotiques et industrielles à Bilbao, ses environs et dans tout le Pays Basque.",
    requestQuote: "Demander un devis",
    services: [{
      id: "urgencias",
      badge: "24/7",
      tag: "URGENCES & DÉPANNAGE",
      title: "Dépannage & Urgences 24/7",
      description: "Recherche et réparation rapide de pannes électriques dans les logements, commerces et industries. Intervention immédiate à Bilbao, ses environs et tout le Pays Basque.",
      highlights: ["Intervention immédiate", "Assistance à Bilbao, environs et Pays Basque", "Diagnostic thermique"]
    }, {
      id: "reformas",
      badge: "RÉSIDENTIEL & TERTIAIRE",
      tag: "NORMES EN VIGUEUR",
      title: "Rénovations Électriques Complètes",
      description: "Rénovation complète des installations en appartements, locaux commerciaux et bureaux, conformes au REBT et à votre consommation réelle.",
      highlights: ["Conformité aux normes REBT", "Optimisation de puissance", "Matériaux haute durabilité"]
    }, {
      id: "cuadros",
      badge: "NORME REBT",
      tag: "ÉLECTRICIENS EXPERTS",
      title: "Tableaux Électriques & Contrôle",
      description: "Conception, montage et homologation de tableaux de distribution et automatismes de haute précision pour toute puissance.",
      highlights: ["Loxone et automatismes", "Protection contre les surtensions", "Assemblage certifié"]
    }, {
      id: "iluminacion",
      badge: "DESIGN LUMIÈRE",
      tag: "EFFICACITÉ LED",
      title: "Éclairage Architectural & LED",
      description: "Projets d'éclairage sur mesure qui subliment les espaces et réduisent la consommation : LED, ambiances et contrôle intelligent.",
      highlights: ["Scénarios d'éclairage", "Intégration domotique", "Économies d'énergie maximales"]
    }, {
      id: "recarga-ve",
      badge: "MOVES III",
      tag: "MOBILITÉ ÉLECTRIQUE",
      title: "Bornes de Recharge VE",
      description: "Installation de bornes pour véhicules électriques en parkings, copropriétés et entreprises. Gestion des subventions MOVES III.",
      highlights: ["Bornes intelligentes", "Gestion des aides MOVES", "Installations en copropriété"]
    }, {
      id: "boletines",
      badge: "INSTALLATEUR AGRÉÉ",
      tag: "CERTIFICATION OFFICIELLE",
      title: "Certificats & Homologations",
      description: "Certificats de conformité électrique, rapports techniques et mise aux normes avec démarches administratives rapides.",
      highlights: ["Certificats CIE / BRIE", "Gestion directe avec l'Industrie", "Contrôle préalable de sécurité"]
    }],
    works: [{
      title: "Automatisation domotique Loxone en maison individuelle",
      category: "Loxone Smart Home"
    }, {
      title: "Rénovation du tableau général et éclairage d'un bureau commercial",
      category: "Installation Commerciale"
    }, {
      title: "Infrastructure de recharge double VE dans un garage privé",
      category: "Bornes de Recharge VE"
    }, {
      title: "Installation d'éclairage LED intégré en angle",
      category: "Éclairage LED"
    }, {
      title: "Intégration domotique Loxone avec audio et réseau",
      category: "Loxone Smart Home"
    }]
  }
};
const serviceIcons: Record<string, React.ElementType> = {
  urgencias: Clock,
  reformas: Zap,
  cuadros: Cpu,
  iluminacion: Lightbulb,
  "recarga-ve": Car,
  boletines: FileCheck
};
const serviceImages: Record<string, {
  src: string;
  srcset: string;
  sizes: string;
  alt: string;
}> = {
  urgencias: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-17.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-17.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-17.png-wvc-sizes",
    alt: "Técnico electricista instalando cuadro eléctrico desde escalera durante una intervención de urgencia"
  },
  reformas: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-14.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-14.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-14.png-wvc-sizes",
    alt: "Instalación eléctrica en reforma integral de vivienda con paredes en obra y vigas de madera vista"
  },
  cuadros: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-15.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-15.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-15.png-wvc-sizes",
    alt: "Cuadro eléctrico de distribución con protecciones Schneider Electric y módulos de automatización Loxone etiquetados"
  },
  iluminacion: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/iluminacion_led_arquitectonica_bilbao.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/iluminacion_led_arquitectonica_bilbao.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/iluminacion_led_arquitectonica_bilbao.png-wvc-sizes",
    alt: "Iluminación LED arquitectónica en un salón moderno de Bilbao"
  },
  "recarga-ve": {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-1.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-1.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-1.png-wvc-sizes",
    alt: "Cargador de vehículo eléctrico instalado en pared con app de gestión Wallbox Manager"
  },
  boletines: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-16.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-16.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/image-16.png-wvc-sizes",
    alt: "Técnico de Electro & Más configurando con ordenador portátil un sistema eléctrico junto al cuadro de distribución"
  }
};
const worksImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG-20260909-WA0001.jpg",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG-20260909-WA0001.jpg-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG-20260909-WA0001.jpg-wvc-sizes",
  alt: "Cuadro eléctrico con módulos de domótica Loxone (fuente de alimentación y Miniserver) en fase de montaje"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20240511_152021.jpg",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20240511_152021.jpg-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20240511_152021.jpg-wvc-sizes",
  alt: "Instalación de cuadro eléctrico general y reforma de iluminación en oficina comercial"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/puntos_recarga_doble_ve_garaje.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/puntos_recarga_doble_ve_garaje.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/puntos_recarga_doble_ve_garaje.png-wvc-sizes",
  alt: "Estación de carga Wallbox en parking residencial de Bizkaia"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/watermarked_img_6199808963100948735.jpg",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/watermarked_img_6199808963100948735.jpg-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/watermarked_img_6199808963100948735.jpg-wvc-sizes",
  alt: "Tira de iluminación LED instalada en esquina de pared con caja de empalme eléctrico visible durante la reforma"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG-20260910-WA0000.jpg",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG-20260910-WA0000.jpg-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/IMG-20260910-WA0000.jpg-wvc-sizes",
  alt: "Cuadro eléctrico con integración de domótica Loxone, audioserver multiroom, red y fibra óptica"
}];
export default function PrecisionServices() {
  const [language] = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.es;
  const [lightboxItem, setLightboxItem] = useState<{
    src: string;
    alt: string;
    title?: string;
  } | null>(null);

  // Lock body scroll and listen for Escape key when lightbox is active
  useEffect(() => {
    if (lightboxItem) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setLightboxItem(null);
      }
    };
    if (lightboxItem) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lightboxItem]);
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section id="servicios-eléctricos-de-precisión" data-nav="dark" className="relative bg-[#0b0f22] text-[#f1f5f9] py-16 md:py-24 border-t border-white/10 overflow-hidden">
      {/* Decorative hairline architectural background pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(#3B82F6_1px,transparent_1px)] [background-size:32px_32px]" aria-hidden="true" />

      <div className="mx-auto max-w-[1904px] px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-4xl mx-auto text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#131a30]/80 border border-[#3B82F6]/30 text-xs font-mono text-[#60A5FA] uppercase tracking-wider mb-4">
            <Sliders className="size-3.5 text-[#60A5FA]" />
            <span>{t.eyebrow}</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight font-sans text-white mb-6">
            {t.heading}
          </h2>

          <p className="text-lg md:text-xl text-[#94a3b8] font-normal leading-relaxed max-w-3xl mx-auto">
            {t.subheading}
          </p>
        </div>

        {/* Loxone Miniserver Spotlight Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-16 rounded-xl border border-[#3B82F6]/30 bg-gradient-to-r from-[#0d1023] via-[#121835] to-[#0d1023] p-6 sm:p-8 relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 -mr-12 -mt-12 w-64 h-64 bg-[#3B82F6]/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8 space-y-3">
              <div className="inline-flex items-center gap-2 text-xs font-mono text-[#60A5FA] font-semibold uppercase tracking-widest">
                <span className="size-2 rounded-full bg-[#3B82F6] animate-pulse" />
                <span>{t.loxoneBadge}</span>
              </div>
              <p className="text-lg sm:text-xl font-semibold text-white leading-snug">
                {t.loxoneDesc1}
              </p>
              <p className="text-sm text-[#94a3b8]">{t.loxoneDesc2}</p>
            </div>

            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end items-stretch sm:items-center lg:items-end">
              <Button asChild className="bg-[#25D366] text-white font-bold hover:bg-[#25D366]/90 h-11 px-6 shadow-lg shadow-[#25D366]/20 rounded-full transition-[color,background-color,box-shadow]">
                <Link to="https://wa.me/34662498828" newTab className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>WhatsApp: 662 49 88 28</span>
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>

        {/* 6 Grid Core Services */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-20">
          {t.services.map((service, index) => {
          const IconComponent = serviceIcons[service.id] || Zap;
          const serviceImg = serviceImages[service.id];
          return <motion.div key={service.id} variants={itemVariants} data-index={index}>
                <Card className="h-full bg-[#101428] border border-white/10 rounded-xl overflow-hidden hover:border-[#3B82F6]/50 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 shadow-xl flex flex-col justify-between group">
                  <div>
                    {/* Image Header with Scrim and Clickable Lightbox Trigger */}
                    <div className="relative h-48 sm:h-52 w-full overflow-hidden bg-[#0d1023] cursor-pointer group/img" onClick={() => setLightboxItem({
                  src: serviceImg.src,
                  alt: serviceImg.alt,
                  title: service.title
                })} role="button" tabIndex={0} aria-label={`Ampliar imagen de ${service.title}`} onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setLightboxItem({
                      src: serviceImg.src,
                      alt: serviceImg.alt,
                      title: service.title
                    });
                  }
                }}>
                      <img src={serviceImg.src} data-wvc-srcset={serviceImg.srcset} data-wvc-sizes={serviceImg.sizes} alt={serviceImg.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#101428] via-[#101428]/40 to-transparent" />

                      {/* Hover expand overlay button */}
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                        <div className="size-10 rounded-full bg-[#0a0e20]/90 border border-[#3B82F6]/60 text-[#60A5FA] flex items-center justify-center shadow-lg">
                          <Maximize2 className="size-5" />
                        </div>
                      </div>

                      {/* Badge Tag */}
                      <div className="absolute top-3 left-3 bg-[#0a0e20]/90 backdrop-blur-md border border-white/10 px-2.5 py-1 rounded-md text-[11px] font-mono text-[#60A5FA] font-semibold uppercase tracking-wider">
                        {service.badge}
                      </div>

                      {/* Floating Icon Box */}
                      <div className="absolute bottom-3 right-3 size-11 bg-[#171e38] border border-white/15 rounded-lg flex items-center justify-center text-[#60A5FA] shadow-md group-hover:border-[#3B82F6]/60 transition-colors">
                        <IconComponent className="size-5" />
                      </div>
                    </div>

                    <CardContent className="p-6">
                      <div className="text-[11px] font-mono text-[#94a3b8] uppercase tracking-wider mb-2">
                        {service.tag}
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3 font-sans group-hover:text-[#60A5FA] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-sm text-[#94a3b8] leading-relaxed mb-6">
                        {service.description}
                      </p>

                      <div className="space-y-2 border-t border-white/5 pt-4">
                        {service.highlights.map((h, i) => <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                            <CheckCircle2 className="size-3.5 text-[#60A5FA] shrink-0" />
                            <span>{h}</span>
                          </div>)}
                      </div>
                    </CardContent>
                  </div>

                  <div className="p-6 pt-0">
                    <Button asChild variant="secondary" className="w-full bg-[#171e38] text-white border border-white/10 hover:bg-[#25D366] hover:text-white font-semibold text-xs h-10 rounded-full justify-between transition-[color,background-color,border-color]">
                      <Link to="https://wa.me/34662498828" newTab>
                        <span>{t.consultService}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Portfolio Showcase Header */}
        <div className="border-t border-white/10 pt-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <div className="text-xs font-mono text-[#60A5FA] uppercase tracking-widest mb-2 flex items-center gap-2">
                <ShieldCheck className="size-4" />
                <span>{t.portfolioEyebrow}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-sans">
                {t.portfolioHeading}
              </h2>
            </div>
            <p className="text-sm text-[#94a3b8] max-w-md">{t.portfolioDesc}</p>
          </div>

          {/* Portfolio Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.works.map((work, idx) => {
            const workImg = worksImages[idx] || worksImages[0];
            return <motion.div key={idx} data-index={idx} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: idx * 0.1
            }} onClick={() => setLightboxItem({
              src: workImg.src,
              alt: workImg.alt,
              title: work.title
            })} role="button" tabIndex={0} aria-label={`Ampliar imagen de ${work.title}`} onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setLightboxItem({
                  src: workImg.src,
                  alt: workImg.alt,
                  title: work.title
                });
              }
            }} className="group relative rounded-xl overflow-hidden bg-[#101428] border border-white/10 aspect-[4/3] cursor-pointer">
                  <img src={workImg.src} data-wvc-srcset={workImg.srcset} data-wvc-sizes={workImg.sizes} alt={workImg.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e20] via-[#0a0e20]/40 to-transparent opacity-90 transition-opacity group-hover:opacity-95" />

                  {/* Hover Expand Icon Badge */}
                  <div className="absolute top-4 right-4 size-9 rounded-lg bg-[#0a0e20]/80 border border-white/15 text-[#60A5FA] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-md">
                    <Maximize2 className="size-4.5" />
                  </div>

                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <span className="text-[11px] font-mono text-[#60A5FA] uppercase tracking-wider mb-1">
                      {work.category}
                    </span>
                    <h3 className="text-base font-bold text-white leading-snug group-hover:text-[#60A5FA] transition-colors">
                      {work.title}
                    </h3>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Direct Contact CTA Footer Bar */}
        <div className="mt-16 text-center">
          <Button asChild size="lg" className="bg-[#3B82F6] text-white hover:bg-[#3B82F6]/90 font-extrabold text-sm h-12 px-8 rounded-full shadow-xl shadow-[#3B82F6]/20 transition-[color,background-color,box-shadow]">
            <Link to="#">
              <span>{t.requestQuote}</span>
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>

      {/* Lightbox Fullscreen Modal */}
      <AnimatePresence>
        {lightboxItem && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 md:p-10 select-none" onClick={() => setLightboxItem(null)}>
            {/* Close Button */}
            <Button type="button" variant="ghost" size="icon" onClick={() => setLightboxItem(null)} className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 rounded-full bg-white/10 text-white hover:bg-white/20 hover:text-white transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#3B82F6]" aria-label="Cerrar imagen">
              <X className="size-6" />
            </Button>

            {/* Lightbox Container */}
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: "easeOut"
        }} className="relative max-w-5xl max-h-[90vh] flex flex-col items-center justify-center" onClick={e => e.stopPropagation()}>
              <img src={lightboxItem.src} alt={lightboxItem.alt} className="max-w-full max-h-[78vh] object-contain rounded-lg shadow-2xl border border-white/10" />
              {(lightboxItem.title || lightboxItem.alt) && <div className="mt-4 px-4 py-2.5 bg-[#101428]/90 border border-white/10 rounded-lg max-w-2xl text-center backdrop-blur-md shadow-lg">
                  {lightboxItem.title && <p className="text-sm sm:text-base font-semibold text-white font-sans">
                      {lightboxItem.title}
                    </p>}
                  {lightboxItem.alt && <p className="text-xs text-[#94a3b8] mt-1 font-sans">
                      {lightboxItem.alt}
                    </p>}
                </div>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}